import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/Modal';
import ModalHeading from '../../components/Modal/ModalHeading';
import ModalForm from '../../components/Modal/ModalForm';
import InputsSection from '../../components/Modal/InputsSection';
import InputBox from '../../components/Modal/InputBox';
import ModalButtons from '../../components/Modal/ModalButtons';
import SelectBox from '../../components/Modal/SelectBox';
import SelectMultiple from '../../components/Modal/SelectMultiple';
import AddUserSkeleton from '../../components/Skeletons/AddUserSkeleton';
import RangeInput from '../../components/Modal/RangeInput';
import { useSelector } from 'react-redux';
import { roleLabel } from '../../utils/roleLabel';

// `assignableRoles` comes from GET /users/roles via UsersScreen — the backend
// decides which roles this tenant may hand out (legacy file-exchange roles
// removed, module-specific roles gated on the tenant's own feature flags).
// This modal used to carry a hardcoded 13-option list which offered MOK
// Client Admin, File Admin, File User, File User (Read Only), Risk Carrier and
// Underwriter — none of which MOK runs. See backend/services/assignableRoles.js.
const AddUserModal = ({ addUserModal, handleAddModalClose, handleAddUser, handleAddFolder, createUserLoading, updateUserLoading, role, totalSize, data, assignableRoles = [] }) => {
  const { t } = useTranslation();
  const {
    userName,
    setUserName,
    userEmail,
    setUserEmail,
    userPhone,
    setUserPhone,
    userAddress,
    setUserAddress,
    userRole,
    setUserRole,
    userSpace,
    setUserSpace,
    userPassword,
    setUserPassword,
    userOrg,
    setUserOrg,
    userFolders,
    orgsList,
    foldersList,
    editUserData,
  } = data;
  // console.log("🚀 ~ AddUserModal ~ foldersList:", foldersList)

  const orgDetails = useSelector(state => state.orgDetails);
  const [clientAdmin, setClientAdmin] = useState(false);

  useEffect(() => {
    if (userRole.value === '' || userRole.value === 'clientAdmin' || userRole.value === 'fileAdmin') setClientAdmin(true);
    else setClientAdmin(false);
  }, [userRole]);

  return (
    <Modal show={addUserModal}>
      <ModalHeading heading={editUserData && editUserData._id ? t('screens.users.modalUpdateTitle') : t('screens.users.modalAddTitle')} />
      {createUserLoading || updateUserLoading ? (
        <AddUserSkeleton />
      ) : (
        <ModalForm style={{ marginBottom: '2.5rem' }}>
          <InputsSection>
            <InputBox
              label={t('screens.users.fieldUserFullName')}
              placeholder={t('screens.users.fieldUserFullNamePh')}
              value={userName.value}
              onChange={event => {
                setUserName({ value: event.target.value, error: '' });
              }}
              errorMessage={userName.error}
            />
            <InputBox
              label={t('screens.users.fieldUserEmail')}
              placeholder={t('screens.users.fieldUserEmailPh')}
              value={userEmail.value}
              onChange={event => {
                setUserEmail({ value: event.target.value, error: '' });
              }}
              errorMessage={userEmail.error}
              disabled={editUserData && editUserData._id}
            />
            <InputBox
              label={t('screens.users.fieldUserPhone')}
              placeholder={t('screens.users.fieldUserPhonePh')}
              value={userPhone.value}
              onChange={event => {
                setUserPhone({ value: event.target.value, error: '' });
              }}
              errorMessage={userPhone.error}
            />
          </InputsSection>

          <InputsSection>
            <InputBox
              label={t('screens.users.fieldUserAddress')}
              placeholder={t('screens.users.fieldUserAddressPh')}
              value={userAddress.value}
              onChange={event => {
                setUserAddress({ value: event.target.value, error: '' });
              }}
              errorMessage={userAddress.error}
            />
            <SelectBox
              label={t('screens.users.fieldUserRole')}
              value={userRole.value}
              onChange={event => {
                setUserRole({ value: event.target.value, error: '' });
                setUserSpace({ value: 0, error: '' });
              }}
              errorMessage={userRole.error}
            >
              <option value=''>{t('screens.users.selectUserRole')}</option>
              {assignableRoles.map(code => (
                <option key={code} value={code}>{roleLabel(t, code)}</option>
              ))}
            </SelectBox>

            {!(editUserData && editUserData._id) && (
              <InputBox
                type='password'
                label={t('screens.users.fieldPassword')}
                placeholder={t('screens.users.fieldPasswordPh')}
                value={userPassword.value}
                onChange={event => {
                  setUserPassword({ value: event.target.value, error: '' });
                }}
                errorMessage={userPassword.error}
              />
            )}
          </InputsSection>

          <InputsSection>
            {role === 'admin' && (
              <SelectBox
                label={t('screens.users.selectClient')}
                value={userOrg.value}
                onChange={event => {
                  setUserOrg({ value: event.target.value, error: '' });
                }}
                errorMessage={userOrg.error}
                disabled={editUserData && editUserData._id}
              >
                <option value=''>{t('screens.users.selectClientPlaceholder')}</option>
                {orgsList && orgsList.orgs && orgsList.orgs.data && orgsList.orgs.data.data && orgsList.orgs.data.data.length > 0 ? (
                  orgsList.orgs.data.data.map(org => (
                    <option value={org._id} key={org._id}>
                      {org.name}
                    </option>
                  ))
                ) : (
                  <option value=''>{t('screens.users.noClientsFound')}</option>
                )}
              </SelectBox>
            )}

            {!clientAdmin && userOrg.value && (
              <SelectMultiple
                label={t('screens.users.selectFolders')}
                handleAddFolder={handleAddFolder}
                userFolders={userFolders}
                loading={foldersList && foldersList.loading}
                loadingText={t('screens.users.loadingFolders')}
                data={foldersList && foldersList.folders && foldersList.folders.data && foldersList.folders.data.data}
                errorMessage={userFolders.error}
              />
            )}
          </InputsSection>

          {!orgDetails.loading && userRole.value === 'fileAdmin' && userOrg.value && (
            <InputsSection>
              <RangeInput
                label={t('screens.users.allocateSpace', { available: (totalSize - orgDetails?.details?.totalAssignedSpace).toFixed(2), total: totalSize })}
                value={userSpace.value * 1024}
                displayValue={userSpace.value.toFixed(2) + ' GB'}
                onChange={event => {
                  setUserSpace({ value: event.target.value / 1024, error: '' });
                }}
                max={
                  editUserData.maxStorageAllowed
                    ? Number(totalSize + editUserData.maxStorageAllowed - orgDetails?.details?.totalAssignedSpace) * 1024
                    : Number(totalSize - orgDetails?.details?.totalAssignedSpace) * 1024
                }
                step={256}
                errorMessage={userSpace.error}
              />
            </InputsSection>
          )}

          <ModalButtons
            submitButtonLabel={orgDetails.loading ? t('screens.users.pleaseWait') : editUserData && editUserData._id ? t('screens.users.updateUserBtn') : t('screens.users.addUserBtn')}
            submitButtonDisabled={orgDetails.loading}
            onSubmit={handleAddUser}
            cancelButtonLabel={t('screens.users.cancel')}
            onCancel={handleAddModalClose}
          />
        </ModalForm>
      )}
    </Modal>
  );
};

export default AddUserModal;
